// ABOUTME: Sets the playbackRate of the breathing scene's keyframe animations: the idle rates at rest, the breath ramp every 100 ms in a round.
// ABOUTME: The animations keep their authored durations, so the canoe and the bands never jump when the rate moves.

import { useEffect, useRef, type RefObject } from 'react';
import { swellPlaybackRate } from '../../crossing/breathMath';

const TICK_MS = 100;
// At rest the canoe and the back band idle on a 6 s cycle (3 s at half speed) and the front band on an 8 s cycle (5 s at 5/8).
const IDLE_SWELL_RATE = 0.5;
const IDLE_FRONT_RATE = 5 / 8;

interface UseSwellPlaybackArgs {
  refs: RefObject<HTMLElement | null>[]; // the elements carrying the swell's 3 s keyframe animation: the canoe and the back band
  frontRef: RefObject<HTMLElement | null>; // the front band, on its own 5 s loop
  mode: 'idle' | 'round' | 'still';
  elapsedMs: () => number;
  total: number; // seconds in the round
  reducedMotion: boolean;
}

// The element's keyframe animation. The bands also carry a transform transition, and a running transition is listed first.
function keyframeAnimation(element: HTMLElement | null): Animation | undefined {
  return element?.getAnimations?.().find(animation => Boolean((animation as CSSAnimation).animationName));
}

function setRate(element: HTMLElement | null, rate: number) {
  const animation = keyframeAnimation(element);
  if (animation) animation.playbackRate = rate;
}

export function useSwellPlayback({ refs, frontRef, mode, elapsedMs, total, reducedMotion }: UseSwellPlaybackArgs): void {
  // "Latest ref" pattern: the effects run once per mode, so they read these instead of closing over them.
  const latest = useRef({ refs, frontRef, elapsedMs, total });
  useEffect(() => { latest.current = { refs, frontRef, elapsedMs, total }; });

  useEffect(() => {
    if (reducedMotion || mode !== 'idle') return;
    latest.current.refs.forEach(element => setRate(element.current, IDLE_SWELL_RATE));
    setRate(latest.current.frontRef.current, IDLE_FRONT_RATE);
  }, [mode, reducedMotion]);

  useEffect(() => {
    if (reducedMotion || mode !== 'round') return;
    setRate(latest.current.frontRef.current, 1);
    const id = setInterval(() => {
      const { refs: elements, elapsedMs: elapsed, total: roundTotal } = latest.current;
      const rate = swellPlaybackRate(elapsed() / 1000, roundTotal);
      elements.forEach(element => setRate(element.current, rate));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [mode, reducedMotion]);
}
